import { Alert, Button, Card, DatePicker, Form, Select, Skeleton, Space, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { filterJobOption } from '../RebuildSnapshots/rebuildSnapshotsModel'
import {
  useJobsWithUndatedWebEntriesQuery,
  useSaveUndatedWebEntryDatesMutation,
  useUndatedWebEntriesQuery,
} from './undatedWebEntriesQueries'
import {
  ChosenDates,
  DateFormat,
  UndatedEntryRow,
  formatCandidateDate,
  getLoadErrorMessage,
  getResultSummary,
  getSaveErrorMessage,
  getSavedMessage,
  itemLabel,
  noUndatedJobsMessage,
  selectedGuidsOn,
  selectedJobIdOn,
  toEntryRows,
  toSaveDatesRequest,
  toUndatedJobOptions,
  toUndatedWebEntriesRequest,
} from './undatedWebEntriesModel'
import { NotAvailable, getJobNotFoundMessage, isJobNotFoundError } from '../adminJobLookupModel'

const { Paragraph, Text } = Typography

type CandidateProps = {
  label: string
  instant: string | null
  disabled: boolean
  onUse: (instant: string) => void
}

const CandidateDate = ({ label, instant, disabled, onUse }: CandidateProps) => {
  if (instant === null) {
    return <Text type="secondary">{NotAvailable}</Text>
  }
  const formatted = formatCandidateDate(instant)
  return (
    <Button
      type="link"
      size="small"
      style={{ padding: 0 }}
      aria-label={`Use ${label} ${formatted}`}
      disabled={disabled}
      onClick={() => onUse(instant)}
    >
      {formatted}
    </Button>
  )
}

const UndatedWebEntriesSection = () => {
  const [tenantId, setTenantId] = useState<number | undefined>(undefined)
  const [pickedJobId, setPickedJobId] = useState<number | undefined>(undefined)
  const [selectedGuids, setSelectedGuids] = useState<string[]>([])
  const [chosenDates, setChosenDates] = useState<ChosenDates>({})
  const tenantsQuery = useSidebarTenantsQuery()
  const jobsQuery = useJobsWithUndatedWebEntriesQuery(tenantId)
  const jobId = selectedJobIdOn(jobsQuery.data, pickedJobId)
  const request = toUndatedWebEntriesRequest({ tenantId, jobId })
  const entriesQuery = useUndatedWebEntriesQuery(request)
  const mutation = useSaveUndatedWebEntryDatesMutation()

  const items = entriesQuery.data?.items ?? []
  const selected = selectedGuidsOn(items, selectedGuids)
  const saveRequest = toSaveDatesRequest(request, items, selected, chosenDates)
  const saving = mutation.isLoading

  const resetRows = () => {
    setSelectedGuids([])
    setChosenDates({})
    mutation.reset()
  }

  const chooseDate = (itemGuid: string, instant: string) => {
    setChosenDates(current => ({ ...current, [itemGuid]: instant }))
  }

  const handleSave = () => {
    if (saveRequest === null) {
      return
    }
    mutation.mutate(saveRequest, {
      onSuccess: () => setSelectedGuids([]),
    })
  }

  const columns = [
    {
      title: 'Item',
      key: 'item',
      render: (_: unknown, entry: UndatedEntryRow) => itemLabel(entry),
    },
    {
      title: 'Last updated',
      key: 'lastUpdated',
      width: 120,
      render: (_: unknown, entry: UndatedEntryRow) => (
        <CandidateDate
          label="last updated"
          instant={entry.lastUpdatedAt}
          disabled={saving}
          onUse={instant => chooseDate(entry.itemGuid, instant)}
        />
      ),
    },
    {
      title: 'Last visit',
      key: 'lastVisit',
      width: 120,
      render: (_: unknown, entry: UndatedEntryRow) => (
        <CandidateDate
          label="last visit"
          instant={entry.lastVisitAt}
          disabled={saving}
          onUse={instant => chooseDate(entry.itemGuid, instant)}
        />
      ),
    },
    {
      title: 'Completed',
      key: 'completed',
      width: 120,
      render: (_: unknown, entry: UndatedEntryRow) => (
        <CandidateDate
          label="completed"
          instant={entry.completedAt}
          disabled={saving}
          onUse={instant => chooseDate(entry.itemGuid, instant)}
        />
      ),
    },
    {
      title: 'Date to save',
      key: 'dateToSave',
      width: 170,
      render: (_: unknown, entry: UndatedEntryRow) => (
        <DatePicker
          aria-label={`Date to save for ${entry.name}`}
          format={DateFormat}
          allowClear={false}
          disabled={saving}
          value={dayjs(entry.dateToSave)}
          onChange={picked => {
            if (picked) {
              chooseDate(entry.itemGuid, picked.toISOString())
            }
          }}
        />
      ),
    },
  ]

  return (
    <Card title="Undated Web Entries" style={{ maxWidth: 1200 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Lists jobs with billable, uninvoiced items that have no visit, no receipt and no web-entry date,
          so they never show on the tenant&apos;s Jobs to Invoice screen. Pick a date for each item from its
          last update, the job&apos;s last visit or the work area&apos;s completion, or type one, then save the
          checked rows.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Tenant">
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={tenantId}
              onChange={(value: number) => {
                setTenantId(value)
                setPickedJobId(undefined)
                resetRows()
              }}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Job">
            <Select
              showSearch
              placeholder={tenantId === undefined ? 'Select a tenant first' : 'Search for a job'}
              value={jobId}
              onChange={(value: number) => {
                setPickedJobId(value)
                resetRows()
              }}
              options={toUndatedJobOptions(jobsQuery.data ?? [])}
              filterOption={filterJobOption}
              loading={jobsQuery.isLoading}
              disabled={tenantId === undefined}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        {jobsQuery.isSuccess && jobsQuery.data.length === 0 && (
          <Alert type="info" showIcon message={noUndatedJobsMessage} />
        )}

        {jobsQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the tenant's jobs"
            description="Change the tenant or re-select it to try again."
          />
        )}

        {mutation.isSuccess && (
          <Alert type="success" showIcon message={getSavedMessage(mutation.data.updatedItemCount)} />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Saving dates failed"
            description={getSaveErrorMessage(mutation.error)}
          />
        )}

        {entriesQuery.isLoading && <Skeleton active />}

        {entriesQuery.isSuccess && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert type="info" showIcon message={getResultSummary(entriesQuery.data)} />
            {items.length > 0 && (
              <>
                <Table<UndatedEntryRow>
                  rowKey="itemGuid"
                  columns={columns}
                  dataSource={toEntryRows(items, chosenDates)}
                  pagination={false}
                  loading={entriesQuery.isFetching}
                  rowSelection={{
                    selectedRowKeys: selected,
                    onChange: keys => setSelectedGuids(keys as string[]),
                    getCheckboxProps: () => ({ disabled: saving }),
                  }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={saving}
                    disabled={saveRequest === null}
                  >
                    Save dates
                  </Button>
                </Space>
              </>
            )}
          </Space>
        )}

        {entriesQuery.isError && isJobNotFoundError(entriesQuery.error) && (
          <Alert
            type="warning"
            showIcon
            message="Job not found"
            description={getJobNotFoundMessage(entriesQuery.error, request)}
          />
        )}

        {entriesQuery.isError && !isJobNotFoundError(entriesQuery.error) && (
          <Alert
            type="error"
            showIcon
            message="Load failed"
            description={getLoadErrorMessage(entriesQuery.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default UndatedWebEntriesSection
